import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/apiConfig';
import { buildPdfShareLink } from '../utils/api';
import '../styles/InvoicePreview.css';
import PaymentTypeModal from '../components/modals/PaymentTypeModal';
import InvoiceTypeModal from '../components/modals/InvoiceTypeModal';
import InvoiceCopyModal from '../components/modals/InvoiceCopyModal';
import { toast } from 'react-toastify';

const SalesReturnPreview = ({ data: initialData, onBack }) => {
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchFullData = async () => {
            const invoiceId = initialData?._id || initialData?.id;
            if (invoiceId) {
                setLoading(true);
                try {
                    const token = sessionStorage.getItem('token');
                    const endpoint = 'sales-return';

                    const res = await axios.get(`${API_BASE_URL}/${endpoint}/${invoiceId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data) {
                        setData({ ...res.data });
                    }
                } catch (err) {
                    console.error("Error fetching full document data:", err);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchFullData();
    }, [initialData]);

    // State for payment types from DB
    const [paymentTypes, setPaymentTypes] = useState([]);

    // State for Payment Type Modal
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [selectedPaymentType, setSelectedPaymentType] = useState(data?.invoicePaymentTypeName || 'Debit');

    useEffect(() => {
        fetchPaymentTypes();
    }, []);

    const fetchPaymentTypes = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/invoice-payment-type`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPaymentTypes(res.data);
            if (!data?.invoicePaymentTypeName && res.data.length > 0) {
                setSelectedPaymentType(res.data[0].invoicePaymentTypeName);
            }
        } catch (err) {
            console.error("Error fetching payment types:", err);
        }
    };

    // State for Invoice Type Modal
    const [isInvoiceTypeModalOpen, setIsInvoiceTypeModalOpen] = useState(false);
    const [selectedInvoiceType, setSelectedInvoiceType] = useState('TAX INVOICE');

    // State for Invoice Copy Modal
    const [isInvoiceCopyModalOpen, setIsInvoiceCopyModalOpen] = useState(false);
    const [selectedCopyType, setSelectedCopyType] = useState('Original');

    // State for WhatsApp
    const [mobileNo, setMobileNo] = useState('');
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);

    // Helper to convert number to words (Indian System)
    const numberToWords = (num) => {
        const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const inWords = (n) => {
            if (n < 20) return a[n];
            let s = b[Math.floor(n / 10)];
            if (n % 10 > 0) s += ' ' + a[n % 10].trim();
            return s;
        };

        const convert = (n) => {
            if (n === 0) return '';
            let res = '';
            if (n >= 10000000) {
                res += convert(Math.floor(n / 10000000)) + 'Crore ';
                n %= 10000000;
            }
            if (n >= 100000) {
                res += inWords(Math.floor(n / 100000)) + ' Lakh ';
                n %= 100000;
            }
            if (n >= 1000) {
                res += inWords(Math.floor(n / 1000)) + ' Thousand ';
                n %= 1000;
            }
            if (n >= 100) {
                res += inWords(Math.floor(n / 100)) + ' Hundred ';
                n %= 100;
            }
            if (n > 0) {
                if (res !== '') res += 'and ';
                res += inWords(Math.floor(n));
            }
            return res;
        };

        if (num === 0) return 'Zero Only';
        const parts = num.toFixed(2).split('.');
        let result = convert(parseInt(parts[0]));
        if (parts.length > 1 && parseInt(parts[1]) > 0) {
            result += ' and ' + inWords(parseInt(parts[1])) + ' Paise';
        }
        return result.trim() + ' Only';
    };

    // Helper to get items as an array
    const getItems = (src) => {
        const raw = src?.items;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string') {
            try { return JSON.parse(raw); } catch (e) { return []; }
        }
        return [];
    };

    // Helper to construct image URL
    const getImageUrl = (path) => {
        if (!path || path === 'undefined' || path === 'null') return '';
        if (typeof path !== 'string') return '';

        if (path.startsWith('data:') || path.startsWith('http')) return path;

        // Handle Base64 strings without prefix
        if (path.length > 500 && !path.includes('/') && !path.includes('\\')) {
            return `data:image/png;base64,${path}`;
        }

        const cleanPath = path.replace(/\\/g, '/').replace(/^public\//, '');
        const relativePath = cleanPath.startsWith('uploads/') ? cleanPath.substring(8) : cleanPath;
        return `${API_BASE_URL}/uploads/${relativePath}`;
    };

    const itemsForCalc = getItems(data);
    const subTotal = parseFloat(data?.sub_total || data?.subTotal || itemsForCalc.reduce((sum, it) => {
        const qty = parseFloat(it.quantity || it.qty || 0);
        const rate = parseFloat(it.rate || it.price || 0);
        return sum + (parseFloat(it.total || it.amount || (qty * rate)) || 0);
    }, 0));

    const isGujarat = (data?.customer_state || data?.customerState || data?.customerPlaceOfSupply || data?.customer_place_of_supply || data?.Customer?.customer_state || '').toLowerCase().includes('gujarat');
    const stateCodeValue = data?.customer_state_code || data?.customerStateCode || data?.Customer?.customer_state_code || (isGujarat ? '24' : '');
    const isLocal = String(stateCodeValue).trim() === '24';

    // Use stored tax rates if available, otherwise fallback to defaults
    let tax1Rate = parseFloat(data?.sale_type_tax1 || (isLocal ? 9 : 18));
    let tax2Rate = parseFloat(data?.sale_type_tax2 || (isLocal ? 9 : 0));

    const discountVal = parseFloat(data?.discount || data?.discountPercent || 0);
    const discountAmt = (data?.discount_type === '%' || data?.discountType === '%') ? (subTotal * discountVal) / 100 : discountVal;

    const insuranceAmt = parseFloat(data?.insurance || 0);
    const insuranceRate = subTotal > 0 ? ((insuranceAmt * 100) / subTotal) : 0;

    const extraAmt = parseFloat(data?.extrChargesAmount || data?.extraChargesAmount || data?.extr_charges_amount || data?.extra_charges_amount || 0);

    const taxableAmount = parseFloat(data?.taxable_amount || data?.taxableAmount || (subTotal - discountAmt + insuranceAmt + extraAmt));

    const tax1Amt = parseFloat(data?.central_tax || data?.centralTax || (taxableAmount * tax1Rate) / 100);
    const tax2Amt = parseFloat(data?.state_tax || data?.stateTax || (taxableAmount * tax2Rate) / 100);
    const totalTaxAmt = tax1Amt + tax2Amt;

    const gTotal = parseFloat(data?.total_amount || data?.totalAmount || (taxableAmount + totalTaxAmt));


    const invoiceData = useMemo(() => {
        const company = data?.Company || data?.companyId || data?.company || {};
        return {
            saleNo: data?.original_invoice_no || '---',
            saleDate: (data?.sale_date || data?.saleDate) ? new Date(data.sale_date || data.saleDate).toLocaleDateString('en-GB') : '---',
            returnNo: data?.return_no || '---',
            returnDate: data?.return_date ? new Date(data.return_date).toLocaleDateString('en-GB') : '---',
            buyer: {
                name: data?.customerName || data?.customer_name || 'N/A',
                address: data?.customerAddress || data?.customer_address || 'N/A',
                cityState: `${data?.customerCity || data?.customer_city || ''}${(data?.customerPinCode || data?.customer_pin_code) ? ' - ' + (data?.customerPinCode || data?.customer_pin_code) : ''}` || 'N/A',
                placeOfSupply: data?.customerPlaceOfSupply || data?.customer_place_of_supply || data?.customerState || data?.customer_state || 'N/A',
                stateCode: stateCodeValue || 'N/A',
                gstin: data?.customerGst || data?.customer_gst || 'N/A',
                pan: data?.customerPanNo || data?.customer_pan_no || 'N/A'
            },
            consignee: {
                name: data?.customerName || data?.customer_name || 'N/A',
                address: data?.customerDeliveryAddress || data?.customer_delivery_address || data?.customerAddress || data?.customer_address || 'N/A',
                cityState: `${data?.customerCity || data?.customer_city || ''}${(data?.customerPinCode || data?.customer_pin_code) ? ' - ' + (data?.customerPinCode || data?.customer_pin_code) : ''}` || 'N/A',
                placeOfSupply: data?.customerPlaceOfSupply || data?.customer_place_of_supply || data?.customerState || data?.customer_state || 'N/A',
                stateCode: stateCodeValue || 'N/A',
                gstin: data?.customerGst || data?.customer_gst || 'N/A',
                pan: data?.customerPanNo || data?.customer_pan_no || 'N/A'
            },
            items: itemsForCalc.map((it, idx) => {
                const qty = parseFloat(it.quantity || it.qty || 0);
                const rate = parseFloat(it.rate || it.price || 0);
                const amount = parseFloat(it.total || it.amount || (qty * rate) || 0);
                return {
                    srNo: idx + 1,
                    category: it.category || '',
                    product: it.product || '',
                    model: it.modelNumber || '',
                    hsn: it.hsnCode || '',
                    grade: it.grade || '',
                    color: it.color || '',
                    qty: qty.toFixed(2),
                    rate: rate.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                    amount: amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })
                };
            }),
            totals: {
                qty: itemsForCalc.reduce((sum, it) => sum + (parseFloat(it.quantity || it.qty) || 0), 0).toFixed(2),
                subTotal: subTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                discount: discountAmt.toFixed(2),
                discountPercent: discountVal,
                discountType: data?.discount_type || data?.discountType || '%',
                taxableAmount: taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                amount: taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 }), // Keep amount alias for compatibility
                words: numberToWords(gTotal),
                insurance: insuranceAmt.toFixed(2),
                insurancePercent: insuranceRate,
                extra: extraAmt.toFixed(2),
                tax1: tax1Amt.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                tax2: tax2Amt.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                totalTax: totalTaxAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                tax1Rate: tax1Rate,
                tax2Rate: tax2Rate,
                isLocal: isLocal,
                grandTotal: gTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 }),
                gstPercent: (tax1Rate + tax2Rate) + '%'
            },
            bank: {
                name: data?.invoiceCompanyBankName || data?.invoice_company_bank_name || company?.company_bank_name || company?.companyBankName || 'N/A',
                accountName: data?.invoiceCompanyBankAccountName || data?.invoice_company_bank_account_name || company?.company_bank_account_name || company?.companyBankAccountName || 'N/A',
                accountNumber: data?.invoiceCompanyBankAccountNumber || data?.invoice_company_bank_account_number || company?.company_bank_account_number || company?.companyBankAccountNumber || 'N/A',
                ifsc: data?.invoiceCompanyBankIfscCode || data?.invoice_company_bank_ifsc_code || company?.company_bank_ifsc_code || company?.companyBankIfscCode || 'N/A'
            }
        };
    }, [data, subTotal, insuranceAmt, extraAmt, totalTaxAmt, gTotal, itemsForCalc, stateCodeValue, tax1Rate, tax2Rate, isLocal, discountVal, discountAmt, taxableAmount]);

    const handlePaymentSubmit = (type) => {
        setSelectedPaymentType(type);
        console.log("Selected Payment Type:", type);
    };

    const handleInvoiceTypeSubmit = (type) => {
        setSelectedInvoiceType(type);
        console.log("Selected Invoice Type:", type);
    };

    const handleInvoiceCopySubmit = (type) => {
        setSelectedCopyType(type);
        console.log("Selected Copy Type:", type);
    };

    // Handle PDF Download
    const handleDownloadPDF = async () => {
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;

        const element = document.querySelector('.invoice-box');

        // Add PDF capture mode to force A4 dimensions for accurate rendering
        element.classList.add('pdf-capture-mode');

        try {
            // Use html2canvas to capture the invoice-box
            const canvas = await html2canvas(element, {
                scale: 2, // Higher scale for better quality
                padding: 0,
                backgroundColor: '#ffffff',
                useCORS: true // Important for images
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');

            const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
            const margin = 4; // 4mm margin on all sides
            const renderWidth = pdfWidth - (margin * 2);

            const imgProps = pdf.getImageProperties(imgData);
            const renderHeight = (imgProps.height * renderWidth) / imgProps.width;

            // Add image to PDF with margin
            pdf.addImage(imgData, 'PNG', margin, margin, renderWidth, renderHeight);

            // Save PDF
            pdf.save(`Invoice_${invoiceData.invoiceNo}.pdf`);
        } finally {
            // Remove PDF capture mode
            element.classList.remove('pdf-capture-mode');
        }
    };

    // Handle PDF Download Without Letter
    const handleDownloadPDFWithoutLetter = async () => {
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;

        const element = document.querySelector('.invoice-box');
        const headerImageContainers = element.querySelectorAll('.header-image-container');
        const signatureContainers = element.querySelectorAll('.auth-sign-img');

        // Store original display styles
        const originalDisplayHeaders = Array.from(headerImageContainers).map(c => c.style.display || '');
        const originalDisplaySignatures = Array.from(signatureContainers).map(c => c.style.display || '');

        // Hide header and signature for capture
        headerImageContainers.forEach(c => c.style.display = 'none');
        signatureContainers.forEach(c => c.style.display = 'none');

        // Force A4 dimensions
        element.classList.add('pdf-capture-mode');

        try {
            // Use html2canvas to capture the invoice-box without header
            const canvas = await html2canvas(element, {
                scale: 2, // Higher scale for better quality
                padding: 0,
                backgroundColor: '#ffffff',
                useCORS: true
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');

            const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
            const margin = 4; // 4mm margin
            const renderWidth = pdfWidth - (margin * 2);

            const imgProps = pdf.getImageProperties(imgData);
            const renderHeight = (imgProps.height * renderWidth) / imgProps.width;

            // Add image to PDF
            pdf.addImage(imgData, 'PNG', margin, margin, renderWidth, renderHeight);

            // Save PDF
            pdf.save(`Invoice_NoLetter_${invoiceData.invoiceNo}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
        } finally {
            // Restore visibility and styling
            element.classList.remove('pdf-capture-mode');
            headerImageContainers.forEach((c, idx) => c.style.display = originalDisplayHeaders[idx]);
            signatureContainers.forEach((c, idx) => c.style.display = originalDisplaySignatures[idx]);
        }
    };

    // Handle Print With Letter
    const handlePrintWithLetter = () => {
        window.print();
    };

    // Handle Print Without Letter
    const handlePrintWithoutLetter = () => {
        const headerContainers = document.querySelectorAll('.header-image-container');
        const signatureContainers = document.querySelectorAll('.auth-sign-img');

        const originalHeaders = Array.from(headerContainers).map(c => c.style.display || '');
        const originalSigns = Array.from(signatureContainers).map(c => c.style.display || '');

        headerContainers.forEach(c => c.style.display = 'none');
        signatureContainers.forEach(c => c.style.display = 'none');

        window.print();

        headerContainers.forEach((c, idx) => c.style.display = originalHeaders[idx]);
        signatureContainers.forEach((c, idx) => c.style.display = originalSigns[idx]);
    };

    // Handle Send WhatsApp
    const handleSendWhatsApp = async () => {
        if (!mobileNo.trim()) {
            toast.error('Please enter a mobile number');
            return;
        }

        let cleaned = mobileNo.replace(/\D/g, '');
        if (cleaned.startsWith('0')) {
            cleaned = cleaned.substring(1);
        } else if (cleaned.length === 10) {
            cleaned = '91' + cleaned;
        }

        const phoneRegex = /^91\d{10}$/;
        if (!phoneRegex.test(cleaned)) {
            toast.error('Invalid mobile number format. Please enter a valid 10-digit number.');
            return;
        }

        const invoiceId = data?._id || data?.id;
        if (!invoiceId) {
            toast.error('Sales Return ID is missing. Cannot send return PDF.');
            return;
        }

        setIsSendingWhatsApp(true);

        try {
            const token = sessionStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE_URL}/sales-return/generate-whatsapp-link`,
                { 
                    invoiceId,
                    invoiceType: selectedInvoiceType,
                    paymentType: selectedPaymentType,
                    copyType: selectedCopyType
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data && response.data.success) {
                const { invoiceNumber, invoiceDate, customerName, totalAmount, pdfUrl } = response.data;
                const companyName = data?.invoice_company_name || data?.Company?.company_name || 'ASENSIA INDUSTRY';

                // Format professional WhatsApp message matching specifications
                const message = `Hello ${customerName},

Thank you for your business.

Sales Return Details:
Return Number: ${invoiceNumber}
Return Date: ${invoiceDate}
Amount: ₹${parseFloat(totalAmount).toLocaleString('en-IN')}

Download Return PDF:
${pdfUrl}

Regards,
${companyName}`;

                // Open WhatsApp Web with dynamic payload
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                const whatsappUrl = isMobile 
                    ? `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`
                    : `https://web.whatsapp.com/send?phone=${cleaned}&text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
                
                toast.success('WhatsApp URL created successfully!');
            } else {
                toast.error(response.data?.message || 'Failed to generate return link.');
            }
        } catch (error) {
            console.error('Error generating WhatsApp shareable link:', error);
            const errorMsg = error.response?.data?.message || 'Server error occurred during PDF generation.';
            toast.error(errorMsg);
        } finally {
            setIsSendingWhatsApp(false);
        }
    };

    if (loading) return <div className="inv-preview-container">Loading...</div>;

    return (
        <div className="inv-preview-container">
            <div className="inv-preview-main">
                <div className="inv-top-controls">
                    <button className="inv-control-btn" onClick={() => setIsPaymentModalOpen(true)}>Payment Type</button>
                    <button className="inv-control-btn" onClick={() => setIsInvoiceTypeModalOpen(true)}>Invoice Type</button>
                    <button className="inv-control-btn" onClick={() => setIsInvoiceCopyModalOpen(true)}>Invoice Copy</button>
                    {onBack && <button className="inv-control-btn" onClick={onBack} style={{ marginLeft: 'auto', background: '#d33' }}>Close Preview</button>}
                </div>

                <div className="invoice-box" id="invoice-content-to-pdf" style={{ fontSize: '12px' }}>
                    {/* Header Image Row */}
                    <div className="header-image-container">
                        <div className="header-image-placeholder">
                            {(() => {
                                const company = data?.Company || data?.companyId || data?.company;
                                // Show letter-head header image only (NOT logo fallback)
                                const headerImg = company?.company_letter_head_header_image || company?.companyLetterHeadHeaderImage;
                                if (headerImg) {
                                    return (
                                        <img
                                            src={getImageUrl(headerImg)}
                                            alt="Company Letter Head"
                                            className="asencia_header"
                                            crossOrigin="anonymous"
                                            style={{ width: '100%', height: 'auto', display: 'block' }}
                                        />
                                    );
                                }
                                // Fallback: clean text company info
                                return (
                                    <div className="no-header-placeholder">
                                        <h1 style={{ margin: 0, fontSize: '20px' }}>{company?.company_name || data?.invoiceCompanyName || data?.invoice_company_name || 'COMPANY NAME'}</h1>
                                        <p style={{ margin: '4px 0 0' }}>{company?.company_address || data?.invoiceCompanyAddress || data?.invoice_company_address || ''}</p>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Meta Header */}
                    <div className="invoice-header-row">
                        <div className="invoice-header-left">{selectedPaymentType.toUpperCase()}</div>
                        <div className="invoice-header-center">{selectedInvoiceType.toUpperCase()}</div>
                        <div className="invoice-header-right">{selectedCopyType.toUpperCase()}</div>
                    </div>

                    {/* Addresses & Meta */}
                    <div className="invoice-addresses" style={{ borderBottom: '1px solid #333' }}>
                        <div className="address-block" style={{ borderBottom: 'none' }}>
                            <div className="address-content" style={{ padding: '5px' }}>
                                <p><strong>M/S. &nbsp;&nbsp;: {invoiceData.buyer.name}</strong></p>
                                <p style={{ paddingLeft: '35px', marginTop: '10px' }}><strong>{invoiceData.buyer.cityState}</strong></p>
                                <p><strong>PLACE OF SUPPLY:</strong> {invoiceData.buyer.placeOfSupply} <strong>MOBILE NO.:</strong> {invoiceData.buyer.mobile || ''}</p>
                                <p><strong>GSTIN NO.:</strong> {invoiceData.buyer.gstin}</p>
                            </div>
                        </div>
                        <div className="address-block" style={{ borderBottom: 'none' }}>
                            <div className="address-content" style={{ padding: '5px' }}>
                                <div className="meta-line"><span className="meta-label">SALE NO.</span>: {invoiceData.saleNo}</div>
                                <div className="meta-line"><span className="meta-label">SALE DATE</span>: {invoiceData.saleDate}</div>
                                <div className="meta-line"><span className="meta-label">RETURN NO.</span>: {invoiceData.returnNo}</div>
                                <div className="meta-line"><span className="meta-label">RETURN DATE</span>: {invoiceData.returnDate}</div>
                            </div>
                        </div>
                    </div>

                    {/* Products Grid */}
                    <div className="invoice-body-grid" style={{ fontSize: '12px', gridTemplateColumns: '40px 1fr 1fr 100px 85px 85px 60px 50px 80px 100px' }}>
                        <div className="grid-header">SR.<br />NO.</div>
                        <div className="grid-header">CATEGORY</div>
                        <div className="grid-header">PRODUCT NAME</div>
                        <div className="grid-header">MODEL NUMBER</div>
                        <div className="grid-header">HSN</div>
                        <div className="grid-header">GRADE</div>
                        <div className="grid-header">COLOR</div>
                        <div className="grid-header">QTY</div>
                        <div className="grid-header">RATE</div>
                        <div className="grid-header">AMOUNT</div>
                    </div>

                    {/* Items */}
                    <div className="invoice-content-container" style={{ display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
                        {invoiceData.items.map((item, i) => (
                            <div key={i} style={{ display: 'flex' }}>
                                <div style={{ width: '40px', borderRight: '1px solid #333', textAlign: 'center', padding: '5px' }}>{item.srNo}</div>
                                <div style={{ flex: 1, borderRight: '1px solid #333', padding: '5px' }}>{item.category}</div>
                                <div style={{ flex: 1, borderRight: '1px solid #333', padding: '5px' }}>{item.product}</div>
                                <div style={{ width: '100px', borderRight: '1px solid #333', textAlign: 'center', padding: '5px' }}>{item.model}</div>
                                <div style={{ width: '85px', borderRight: '1px solid #333', textAlign: 'center', padding: '5px' }}>{item.hsn}</div>
                                <div style={{ width: '85px', borderRight: '1px solid #333', textAlign: 'center', padding: '5px' }}>{item.grade}</div>
                                <div style={{ width: '60px', borderRight: '1px solid #333', textAlign: 'center', padding: '5px' }}>{item.color}</div>
                                <div style={{ width: '50px', borderRight: '1px solid #333', textAlign: 'center', padding: '5px' }}>{item.qty}</div>
                                <div style={{ width: '80px', borderRight: '1px solid #333', textAlign: 'right', padding: '5px' }}>{item.rate}</div>
                                <div style={{ width: '100px', textAlign: 'right', padding: '5px' }}>{item.amount}</div>
                            </div>
                        ))}
                        {/* Filler row for continuous vertical lines */}
                        <div style={{ flex: 1, display: 'flex' }}>
                            <div style={{ width: '40px', borderRight: '1px solid #333' }}></div>
                            <div style={{ flex: 1, borderRight: '1px solid #333' }}></div>
                            <div style={{ flex: 1, borderRight: '1px solid #333' }}></div>
                            <div style={{ width: '100px', borderRight: '1px solid #333' }}></div>
                            <div style={{ width: '85px', borderRight: '1px solid #333' }}></div>
                            <div style={{ width: '85px', borderRight: '1px solid #333' }}></div>
                            <div style={{ width: '60px', borderRight: '1px solid #333' }}></div>
                            <div style={{ width: '50px', borderRight: '1px solid #333' }}></div>
                            <div style={{ width: '80px', borderRight: '1px solid #333' }}></div>
                            <div style={{ width: '100px' }}></div>
                        </div>
                    </div>

                    {/* Total Line */}
                    {/* We need to match the columns precisely for the total line or use a flex row */}
                    <div style={{ display: 'flex', borderTop: '1px solid #333', borderBottom: '1px solid #333', fontWeight: 'bold' }}>
                        <div style={{ flex: 1, borderRight: '1px solid #333', padding: '5px' }}>
                            {invoiceData.totals.isLocal ? 'LOCAL SALES' : 'INTER STATE SALES'} {invoiceData.totals.gstPercent}
                        </div>
                        <div style={{ flex: 1, textAlign: 'right', borderRight: '1px solid #333', padding: '5px' }}>TOTAL</div>
                        <div style={{ width: '50px', borderRight: '1px solid #333', textAlign: 'center', padding: '5px' }}>{invoiceData.totals.qty}</div>
                        <div style={{ width: '80px', borderRight: '1px solid #333', padding: '5px' }}></div>
                        <div style={{ width: '100px', textAlign: 'right', padding: '5px' }}>{invoiceData.totals.subTotal}</div>
                    </div>


                    {/* Footer Lower Section */}
                    <div className="footer-row no-border">
                        <div className="footer-col-left">
                            <div className="remark-row"><strong>REMARK</strong></div>
                            <div className="amount-words-row">RUPPES IN WORDS :- {invoiceData.totals.words}</div>

                            <table className="bank-details-table">
                                <tbody>
                                    <tr>
                                        <td width="150" style={{ fontWeight: 'bold', fontSize: '12px' }}>BANK NAME</td>
                                        <td style={{ fontSize: '12px' }}>{invoiceData.bank.name}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ fontWeight: 'bold', fontSize: '12px' }}>ACCOUNT_NAME</td>
                                        <td style={{ fontSize: '12px' }}>{invoiceData.bank.accountName}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ fontWeight: 'bold', fontSize: '12px' }}>ACCOUNT_NUMBER</td>
                                        <td style={{ fontSize: '12px' }}>{invoiceData.bank.accountNumber}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ fontWeight: 'bold', fontSize: '12px' }}>RTGS/IFSC CODE</td>
                                        <td style={{ fontSize: '12px' }}>{invoiceData.bank.ifsc}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style={{ borderTop: '1px solid #333', padding: '5px', fontWeight: 'bold' }}>
                                TAX IS PAYABLE ON REVERSE CHARGE : NO
                            </div>
                        </div>
                        <div className="footer-col-right">
                            <table className="summary-table">
                                <tbody>
                                    <tr>
                                        <td>SUB TOTAL</td>
                                        <td align="right">{invoiceData.totals.subTotal}</td>
                                    </tr>
                                    {parseFloat(invoiceData.totals.discount) > 0 && (
                                        <tr>
                                            <td>DISCOUNT ({invoiceData.totals.discountPercent}{invoiceData.totals.discountType})</td>
                                            <td align="right">-{invoiceData.totals.discount}</td>
                                        </tr>
                                    )}
                                    {parseFloat(invoiceData.totals.insurance) > 0 && (
                                        <tr>
                                            <td>INSURANCE ({parseFloat(invoiceData.totals.insurancePercent).toFixed(2)}%)</td>
                                            <td align="right">{invoiceData.totals.insurance}</td>
                                        </tr>
                                    )}
                                    {parseFloat(invoiceData.totals.extra) > 0 && (
                                        <tr>
                                            <td>{data?.extraChargesName || data?.extra_charges_name || 'EXTRA CHARGES'}</td>
                                            <td align="right">{invoiceData.totals.extra}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>TAXABLE AMOUNT</td>
                                        <td align="right">{invoiceData.totals.taxableAmount}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan="2" style={{ borderBottom: '1px solid #eee' }}></td>
                                    </tr>
                                    {invoiceData.totals.isLocal ? (
                                        <>
                                            <tr>
                                                <td>CGST ({invoiceData.totals.tax1Rate}%)</td>
                                                <td align="right">{invoiceData.totals.tax1}</td>
                                            </tr>
                                            <tr>
                                                <td>SGST ({invoiceData.totals.tax2Rate}%)</td>
                                                <td align="right">{invoiceData.totals.tax2}</td>
                                            </tr>
                                        </>
                                    ) : (
                                        <tr>
                                            <td>IGST ({invoiceData.totals.tax1Rate + invoiceData.totals.tax2Rate}%)</td>
                                            <td align="right">{invoiceData.totals.totalTax}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>GRAND TOTAL</td>
                                        <td align="right"><b>{invoiceData.totals.grandTotal}</b></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Terms and Signature */}
                    <div style={{ borderTop: '1px solid #333', display: 'flex' }}>
                        <div className="terms-col" style={{ fontSize: '12px' }}>
                            <strong>TERMS & CONDITION</strong>
                            <ol style={{ paddingLeft: '20px', marginTop: '5px' }}>
                                <li>GOODS ONCE SUPPLIED WILL NOT BE TAKEN BACK OR EXCHANGED.</li>
                                <li>WE ARE NOT RESPONSIBLE FOR ANY SHORTAGE OR DAMAGE IN TRANSIST.</li>
                                <li>INTEREST AT THE RATE OF 18% SHALL BE CHARGED ON ALL AMOUNTS UNPAID WITHIN 15 DAYS FROM THE SUPPLY DATE.</li>
                                <li>RS. 250 WILL BE CHARGED, WHEN CHEQUE WOULD BOUNCE.</li>
                                <li>SUBJECT TO MORBI JURISDICTION. E. & O.E</li>
                            </ol>
                        </div>
                        <div className="signature-col" style={{ borderLeft: '1px solid #333' }}>
                            <div style={{ fontWeight: 'bold' }}>FOR, ASENSIA INDUSTRY LLP</div>
                            <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {(() => {
                                    const company = data?.Company || data?.companyId || data?.company;
                                    const sigImg = company?.company_digital_signature || company?.companyDigitalSignature;
                                    if (sigImg) {
                                        return (
                                            <img
                                                src={getImageUrl(sigImg)}
                                                alt="Authorized Signatory"
                                                className="auth-sign-img"
                                                crossOrigin="anonymous"
                                            />
                                        );
                                    }
                                    return <div style={{ fontStyle: 'italic', color: '#ccc' }}>Sign here</div>;
                                })()}
                            </div>
                            <div style={{ fontSize: '10px' }}>(AUTHORISED SIGNATORY)</div>
                        </div>
                    </div>
                    {/* Footer Image Row */}
                    {(() => {
                        const company = data?.Company || data?.companyId || data?.company;
                        const footerImg = company?.company_letter_head_footer_image || company?.companyLetterHeadFooterImage;
                        if (footerImg) {
                            return (
                                <div className="footer-image-container" style={{ borderTop: '1px solid #333' }}>
                                    <img
                                        src={getImageUrl(footerImg)}
                                        alt="Footer"
                                        style={{ width: '100%', height: 'auto', maxHeight: '100px' }}
                                        crossOrigin="anonymous"
                                    />
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
            </div>

            <div className="inv-sidebar">
                <button className="sidebar-btn" onClick={handleDownloadPDF}>With Letter PDF</button>
                <button className="sidebar-btn" onClick={handleDownloadPDFWithoutLetter}>Without Letter PDF</button>
                <button className="sidebar-btn" onClick={handlePrintWithLetter}>With Letter Print</button>
                <button className="sidebar-btn" onClick={handlePrintWithoutLetter}>Without Letter Print</button>


                <input
                    type="text"
                    placeholder="Enter Mobile Number"
                    className="sidebar-input"
                    value={mobileNo}
                    onChange={(e) => setMobileNo(e.target.value)}
                />
                <button
                    className="sidebar-btn sidebar-btn-whatsapp"
                    onClick={handleSendWhatsApp}
                    disabled={isSendingWhatsApp}
                >
                    {isSendingWhatsApp ? 'Sending...' : 'Send WhatsApp'}
                </button>
            </div>

            {/* Payment Type Modal */}
            <PaymentTypeModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                onSubmit={handlePaymentSubmit}
                currentType={selectedPaymentType}
            />

            {/* Invoice Type Modal */}
            <InvoiceTypeModal
                isOpen={isInvoiceTypeModalOpen}
                onClose={() => setIsInvoiceTypeModalOpen(false)}
                onSubmit={handleInvoiceTypeSubmit}
                currentType={selectedInvoiceType}
            />

            {/* Invoice Copy Modal */}
            <InvoiceCopyModal
                isOpen={isInvoiceCopyModalOpen}
                onClose={() => setIsInvoiceCopyModalOpen(false)}
                onSubmit={handleInvoiceCopySubmit}
            />
        </div>
    );
};

export default SalesReturnPreview;
